// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Loader from "src/components/shared/loaders/Loader/Loader";
import ComparisonImage from "./ComparisonImage/ComparisonImage";
import { ChevronLeftIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomCancel,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { useTheme } from "@mui/material/styles";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Hooks
import { useExternalExamById } from "src/services/exams/hooks/external-exams/useExternalExamById";

type ExamDetailsScreenType = {
  examId: string;
  onBack: () => void;
};

const ExamDetailsScreen = ({ examId, onBack }: ExamDetailsScreenType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, dividerBorder } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const { selectedPatient } = useExternalExamsContext();

  const { data: externalExamData, isLoading } = useExternalExamById(
    clinicInfo?.id,
    {
      patientId: selectedPatient?.id,
      examId,
    }
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: "53px",
          minHeight: "53px",
          width: "100%",
          borderBottom: dividerBorder,
          px: "16px",
          backgroundColor: surface,
        }}
      >
        <Button
          disableRipple
          startIcon={<ChevronLeftIcon />}
          onClick={onBack}
          sx={ModalCustomCancel}
        >
          {t("Back")}
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loader size="60px" containerProps={{ height: "60px" }} />
          </Box>
        ) : externalExamData ? (
          <ComparisonImage selectedExam={externalExamData} />
        ) : (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography>{t("No image preview available.")}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExamDetailsScreen;
